/**
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import imagesLoaded from 'imagesloaded';
import 'lazysizes';
import { debounce } from 'perfect-debounce';
import Vue from 'vue';
import zenscroll from 'zenscroll';
import './bootstrap';
import './components';
import './directives';
import { DropdownAccessibilityMixin } from './mixins/dropdown-accessibility-mixin';
import { NavigationAccessibilityMixin } from './mixins/navigation-accessibility-mixin';
import './swiper-utils';
import {
    checkCCM19Consent,
    checkOneTrustConsent,
    handleSetUtmCookie,
} from './utils';

lazySizes.cfg.blurupMode = 'auto';

async function initVueJS() {
    window.EventBus = new Vue();

    window.mapsCallback = () => {
        EventBus.$emit('google-maps-ready');
    };

    // eslint-disable-next-line no-unused-vars
    window.MGApp = new Vue({
        mixins: [DropdownAccessibilityMixin, NavigationAccessibilityMixin],
        el: '#app',
        delimiters: ['${', '}'],

        data() {
            return {
                showMenu: false,
                showSubmenu: false,
                showCountries: false,
                hasInteracted: false,
                handle: 'default',
                languageCode: null,
                darkColorSchemeSwitch: false,
                countdownDeadlineReached: false,

                isCustomEuRedirect: false,
                isRedirectToConfigurator: false,
                isRedirectToTestdrive: false,
                searchParams: {},
                redirectFlagTimeout: null,

                drawerItemHover: 0,
                drawerItemHoverHelper: 0,
                drawerItemHoverTimeout: null,
                drawerItemHoverTime: 300,
                drawerSubGroup: 0,
                drawerModel: 0,

                accordionIndex: null,
                isMobile: false,
            };
        },

        watch: {
            showMenu: function (visible) {
                EventBus.$emit('navigation-state-changed', visible);

                if (!visible) {
                    gsap.set(document.body, {
                        overflow: null,
                    });
                    return;
                }

                gsap.set(document.body, {
                    overflow: 'hidden',
                });
            },

            showCountries: function (visible) {
                if (this.showMenu) return;
                document.body.style.overflow = visible ? 'hidden' : 'auto';
            },
        },

        computed: {
            toggleFixedClass() {
                return this.showMenu || this.showCountries ? 'fixed' : '';
            },
            toggleWhiteLogoClass() {
                return this.darkColorSchemeSwitch ? 'logo--white' : '';
            },
            toggleNavBackgroundClass() {
                return this.showMenu ? 'main-navigation--background' : '';
            },
            toggleNavWhiteClass() {
                return this.darkColorSchemeSwitch
                    ? 'main-navigation--white'
                    : '';
            },
            toggleNavVisibleclass() {
                return this.showMenu ? 'nav--visible' : '';
            },

            toggleCountriesClass() {
                return this.showCountries ? 'countries--visible' : '';
            },
        },

        created() {
            if (!Modernizr.picture) {
                // Picture element HTML5 shiv
                document.createElement('picture');

                // Inject the polyfill
                const picturefill = document.createElement('script');
                picturefill.src =
                    'https://cdn.rawgit.com/scottjehl/picturefill/3.0.2/dist/picturefill.min.js';
                picturefill.setAttribute('type', 'text/javascript');
                picturefill.async = true;
                picturefill.defer = true;
                document.head.appendChild(picturefill);
            }
        },

        async mounted() {
            window.addEventListener('resize', this.onWindowResize);
            zenscroll.setup(999, 0);

            this.setCookies();

            this.isMobile = window.innerWidth < 768;

            this.languageCode = document
                .querySelector('html')
                .getAttribute('lang');

            this.handle = document
                .querySelector('body')
                .getAttribute('data-handle');
            gsap.registerPlugin(ScrollTrigger);

            new imagesLoaded(this.$el, () => {
                this.initRevealTriggers();
            });

            // Check interaction for FaceBook Pixel
            window.addEventListener(
                'click',
                this.personHasInteracted.bind(this),
            );
            window.addEventListener(
                'scroll',
                this.personHasInteracted.bind(this),
            );

            this.removeInertOnCCM19Consent();

            // Track cta click for Italy
            this.trackClickCta();

            if (checkCCM19Consent() || checkOneTrustConsent()) {
                setTimeout(() => {
                    if (this.hasInteracted) {
                        // check if you are on production-enviroment

                        // TODO: Remove or uncomment later
                        // fbq('trackCustom', 'QualifiedTraffic');
                        gtag('consent', 'update', {
                            ad_storage: 'granted',
                            analytics_storage: 'granted',
                            ad_user_data: 'granted',
                            ad_personalization: 'granted',
                        });

                        if (this.handle == 'mgMotorsFrance') {
                            gtag('event', 'conversion', {
                                allow_custom_scripts: true,
                                send_to: 'DC-10256326/mg-autos/no_in0+unique',
                            }); //French
                        }

                        if (this.handle == 'mgMotorsNorway') {
                            gtag('event', 'conversion', {
                                allow_custom_scripts: true,
                                u1: this.handle,
                                u2: location.pathname,
                                send_to: 'DC-10210913/retar0/no_re00+standard',
                            });
                            snaptr('track', 'CUSTOM_EVENT_4');
                        }

                        if (this.handle == 'mgMotorsNetherlands') {
                            this.insertTrackingImageLinkedin(
                                'https://px.ads.linkedin.com/collect/?pid=2629129&conversionId=2956258&fmt=gif',
                            );
                        }
                    }
                }, 10000);
            }

            EventBus.$on('deadline-has-expired', (value) => {
                this.countdownDeadlineReached = value;
            });

            Vue.nextTick(() => {
                let scrollToEl = null;
                if (window.location.hash) {
                    try {
                        scrollToEl = document.querySelector(
                            `${window.location.hash}`,
                        );
                    } catch (error) {}
                }
                if (scrollToEl) {
                    setTimeout(() => {
                        zenscroll.to(scrollToEl);
                    }, 1000);
                }
            });
        },

        updated() {
            ScrollTrigger.refresh();
        },

        methods: {
            trackClickCta() {
                if (!window || this.handle !== 'mgMotorsItaly') return;
                if (!window.dataLayer) {
                    window.dataLayer = window.dataLayer || [];
                }

                window.addEventListener('click', (e) => {
                    const link = e.target.closest('a');
                    if (!link) return;

                    const originUrl = window.location.href;
                    const isNoTrackingLink = link.getAttribute(
                        'data-no-tracking-link',
                    );

                    if (isNoTrackingLink) return;

                    const ismartDataAttribute = link.getAttribute(
                        'data-ismart-attribute',
                    );

                    window.dataLayer.push({
                        event: 'click_cta',
                        event_id: '001',
                        page_type: originUrl,
                        clicked_cta: ismartDataAttribute
                            ? 'MG iSMART'
                            : link.innerText,
                        click_url: link.href,
                    });
                });
            },

            removeInertOnCCM19Consent() {
                // select all button on ccm cookie consent view that either decline or accept the cookies
                const cookieSubmitButtons = document.querySelectorAll([
                    '[type="button"].ccm--save-settings',
                    '[type="button"].ccm--decline-cookies',
                ]);

                if (cookieSubmitButtons.length) {
                    // ccm script would not remove the inert attribute from the app element after the consent is accepted/declined
                    cookieSubmitButtons.forEach((button) => {
                        button.addEventListener('click', () => {
                            if (document.getElementById('app')) {
                                document
                                    .getElementById('app')
                                    .removeAttribute('inert');
                            }
                        });
                    });
                }
            },

            setCookies() {
                const location = window.location.href;

                if (location.includes('utm_')) {
                    const params = new URLSearchParams(location.split('?')[1]);
                    handleSetUtmCookie({
                        params,
                        cookieName: 'utm_source',
                    });
                    handleSetUtmCookie({
                        params,
                        cookieName: 'utm_medium',
                    });
                    handleSetUtmCookie({
                        params,
                        cookieName: 'utm_campaign',
                    });
                    handleSetUtmCookie({
                        params,
                        cookieName: 'utm_content',
                    });
                    handleSetUtmCookie({
                        params,
                        cookieName: 'utm_term',
                    });
                    handleSetUtmCookie({ params, cookieName: 'dod_id' });
                    return;
                }

                // Referrer-based UTM fallback (HU & PL only)
                const siteHandle = document
                    .querySelector('body')
                    .getAttribute('data-handle');

                const isSpecialUtmReferrerCountry = [
                    'mgMotorsHungary',
                    'mgMotorsPoland',
                ].includes(siteHandle);

                if (!isSpecialUtmReferrerCountry) return;

                // Don't overwrite existing UTM cookies
                if (document.cookie.includes('/utm/utm_source=')) return;

                const utmData = this.deriveUtmFromReferrer();
                if (!utmData) return;

                const params = new URLSearchParams(utmData);
                handleSetUtmCookie({ params, cookieName: 'utm_source' });
                handleSetUtmCookie({ params, cookieName: 'utm_medium' });
                handleSetUtmCookie({
                    params,
                    cookieName: 'utm_campaign',
                });
            },

            deriveUtmFromReferrer() {
                const referrer = document.referrer;

                if (!referrer) return null;

                try {
                    const referrerUrl = new URL(referrer);
                    const referrerHostname = referrerUrl.hostname;

                    // Exclude same-domain and cross-country MG Motor referrers
                    if (
                        referrerHostname === window.location.hostname ||
                        referrerHostname.includes('mgmotor')
                    ) {
                        return null;
                    }

                    const searchEngines = [
                        'google',
                        'bing',
                        'yahoo',
                        'duckduckgo',
                        'baidu',
                        'yandex',
                        'ecosia',
                        'ask',
                        'aol',
                        'seznam',
                    ];

                    const engineMatch = searchEngines.find((engine) =>
                        referrerHostname.includes(engine),
                    );

                    if (engineMatch) {
                        return {
                            utm_source: engineMatch,
                            utm_medium: 'organic',
                            utm_campaign: '',
                        };
                    }

                    // Referral traffic
                    return {
                        utm_source: referrerHostname,
                        utm_medium: 'referral',
                        utm_campaign: '',
                    };
                } catch (e) {
                    return null;
                }
            },

            handleClassIfConfiguratorOrEuropeAreSelected(siteHandle) {
                if (this.handle !== 'default') return;
                if (siteHandle === 'default' && this.isCustomEuRedirect) {
                    return 'countries-navigation__item--deactivated';
                }
            },
            toggleHamburgerClass(hamburgerPositionClass) {
                const isMenuOpen = this.showMenu || this.showCountries;
                return isMenuOpen ? hamburgerPositionClass : '';
            },

            toggleCountrySelectorClass(countryActiveClass) {
                return this.showCountries ? countryActiveClass : '';
            },

            setDrawerItemHover(index) {
                this.drawerItemHoverTimeout = setTimeout(() => {
                    if (index != this.drawerItemHover) {
                        this.drawerItemHover = index;
                        gsap.fromTo(
                            '.drawer__group',
                            { opacity: 0 },
                            { opacity: 1 },
                        );
                        gsap.fromTo(
                            '.drawer__group-model',
                            { opacity: 0 },
                            { opacity: 1 },
                        );
                    }
                }, this.drawerItemHoverTime);
            },

            setDrawerItemLeave() {
                clearTimeout(this.drawerItemHoverTimeout);
            },

            setAccordionIndex(index) {
                if (index != this.accordionIndex) {
                    this.accordionIndex = index;
                } else {
                    this.accordionIndex = null;
                }
            },

            updateMenu() {
                this.showMenu = !this.showMenu;
                this.handleCleanQueryParams();
            },

            handleQueryParamsForEuConfigurator(e) {
                const queryParams =
                    e.srcElement.dataset.queryParams.split('?')[1];
                history.replaceState(
                    undefined,
                    undefined,
                    `${window.location.pathname}?${queryParams}`,
                );

                this.isCustomEuRedirect = true;
                this.isRedirectToConfigurator =
                    queryParams.includes('openConfigurator');
                this.isRedirectToTestdrive =
                    queryParams.includes('openTestdrive');
                this.showCountries = true;
            },

            handleCleanQueryParams() {
                if (this.redirectFlagTimeout) {
                    clearTimeout(this.redirectFlagTimeout);
                }

                this.searchParams = Object.fromEntries(
                    new URLSearchParams(window.location.search).entries(),
                );
                this.isCustomEuRedirect =
                    ('openConfigurator' in this.searchParams ||
                        'openTestdrive' in this.searchParams) &&
                    'model' in this.searchParams;

                if (this.isCustomEuRedirect) {
                    history.replaceState(
                        undefined,
                        undefined,
                        window.location.pathname,
                    );

                    // This flag needs to be set to false after a short delay
                    // because country selector has a transition before it's fully hidden
                    this.redirectFlagTimeout = setTimeout(() => {
                        this.isCustomEuRedirect = false;
                        this.isRedirectToConfigurator = false;
                        this.isRedirectToTestdrive = false;
                    }, 250);
                }
            },

            updateCountriesMenu() {
                this.showCountries = !this.showCountries;
                this.handleCleanQueryParams();

                const isNavigationDrawerLeftVisible =
                    !this.isCustomEuRedirect ||
                    (this.isCustomEuRedirect && this.showMenu);

                if (isNavigationDrawerLeftVisible) return;
                this.showMenu = false;
            },

            setDrawerModel(index) {
                if (index != this.drawerModel) {
                    this.drawerModel = index;
                }
            },

            setDrawerSubGroup(index) {
                if (index != this.drawerSubGroup) {
                    this.drawerSubGroup = index;
                }
            },

            trackDownloadBrochure(
                editionType,
                siteHandle,
                languageCode,
                pageUrl,
                modelName,
            ) {
                const strippedModelName = modelName.replace(
                    /(<([^>]+)>)/gi,
                    '',
                );
                const country = (siteHandle = 'default'
                    ? 'mgMotorsEurope'
                    : siteHandle);

                if (checkCCM19Consent() || checkOneTrustConsent()) {
                    // check if you are on production-enviroment
                    if (!window.dataLayer) {
                        window.dataLayer = window.dataLayer || [];
                    }

                    dataLayer.push({
                        event: 'Download brochure',
                        model: strippedModelName,
                        edition: editionType,
                        page_path: pageUrl,
                        country: `${country} - ${languageCode}`,
                    });

                    gtag('event', 'conversion', {
                        allow_custom_scripts: true,
                        u1: siteHandle,
                        u2: siteHandle,
                        u3: pageUrl,
                        u4: modelName,
                        send_to: 'DC-10256359/mg_le0/mg_le000+standard',
                    }); //nl

                    if (this.handle == 'mgMotorsFrance') {
                        // datalayer
                        dataLayer.push({
                            event: 'interestAction',
                            eventCategory: 'Interest',
                            eventAction: 'Download brochure',
                            eventLabel: strippedModelName,
                        });

                        var randomNumber =
                            Math.floor(Math.random() * 10000) + 1; // used in france so a unique number is set to the transaction_id

                        gtag('event', 'conversion', {
                            allow_custom_scripts: true,
                            u1: modelName,
                            send_to: 'DC-10256326/mg-autos/fr_in00+unique',
                        });
                        gtag('event', 'purchase', {
                            allow_custom_scripts: true,
                            value: '0',
                            transaction_id: randomNumber,
                            send_to: 'DC-10256326/conv/fr_in000+transactions',
                        }); //French
                    }

                    if (this.handle == 'mgMotorsNorway') {
                        gtag('event', 'conversion', {
                            allow_custom_scripts: true,
                            u1: siteHandle,
                            u2: editionType,
                            u3: pageUrl,
                            u4: modelName,
                            send_to: 'DC-10210913/lead40/no_in00+standard',
                        }); //norway
                        gtag('event', 'conversion', {
                            allow_custom_scripts: true,
                            send_to: 'DC-10210913/lead40/no_pd0+standard',
                        }); // Norway - NO_INI_Lead_PDF Download
                        snaptr('track', 'CUSTOM_EVENT_1', {
                            item_category: modelName,
                        });
                    }

                    if (this.handle == 'mgMotorsSpain') {
                        var axel = Math.random() + '';
                        var a = axel * 10000000000000;
                        document.write(
                            '<iframe src="https://10256359.fls.doubleclick.net/activityi;src=10256359;type=mg_le0;cat=mg_le000;u1=' +
                                siteHandle +
                                ';u2=' +
                                siteHandle +
                                ';u3=' +
                                pageUrl +
                                ';u4=' +
                                pageUrl +
                                ';dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;npa=;gdpr=${GDPR};gdpr_consent=${GDPR_CONSENT_755};ord=' +
                                a +
                                '?" width="1" height="1" frameborder="0" style="display:none"></iframe>',
                        );
                    }

                    // TODO: Remove or uncomment later
                    // fbq('trackCustom', 'BrochureDownload', {
                    //     Content_type: modelName,
                    // }); //Globally (Original Dutch)

                    if (this.handle == 'mgMotorsNetherlands') {
                        this.insertTrackingImageLinkedin(
                            'https://px.ads.linkedin.com/collect/?pid=2629129&conversionId=2956306&fmt=gif',
                        );

                        // Trade deck
                        const tradeDeckCodes = {
                            'mg zs ev': 'tocwpe',
                            'mg ehs': 'o8vnc0k',
                        };

                        const model = strippedModelName.toLowerCase();

                        if (tradeDeckCodes[model]) {
                            const img = document.createElement('img');
                            img.style.height = '1';
                            img.style.width = '1';
                            img.style.borderStyle = 'none';
                            img.src = `https://insight.adsrvr.org/track/pxl/?adv=teshga3&ct=0:${trackDeckCodes[model]}&fmt=3`;
                            document.body.appendChild(img);
                        }
                    }

                    if (
                        this.handle == 'mgMotorsBelgiumFr' ||
                        this.handle == 'mgMotorsBelgiumNl'
                    ) {
                        _tfa.push({
                            notify: 'event',
                            name: 'Brochure',
                            id: 1384899,
                        });
                    }
                }
            },

            requestOfferFormTracking(
                url,
                model,
                siteHandle,
                languageCode,
                color,
                edition,
                engineType,
                paymentName,
            ) {
                const country = (siteHandle = 'default'
                    ? 'mgMotorsEurope'
                    : siteHandle);

                if (checkCCM19Consent() || checkOneTrustConsent()) {
                    // check if you are on production-enviroment
                    if (!window.dataLayer) {
                        window.dataLayer = window.dataLayer || [];
                    }

                    if (this.handle == 'mgMotorsGermany') {
                        gtag('event', 'conversion', {
                            allow_custom_scripts: true,
                            u1: url,
                            u2: model,
                            send_to: 'DC-10844609/engag0/autok0+standard',
                        });
                    }
                }
            },

            requestContactFormTracking(
                url,
                referrer,
                siteHandle,
                languageCode,
            ) {
                const country = (siteHandle = 'default'
                    ? 'mgMotorsEurope'
                    : siteHandle);

                if (checkCCM19Consent() || checkOneTrustConsent()) {
                    // check if you are on production-enviroment
                    // Daisycon pixel
                    if (!window.dataLayer) {
                        window.dataLayer = window.dataLayer || [];
                    }

                    window.dataLayer.push({
                        'campaign-id': '16022',
                        'transaction-id': '19705',
                        'product-name': 'MG',
                        amount: '0',
                        revenue: '0',
                        'compensation-code': 'Contact',
                        e1: `${referrer}`,
                    });
                }
            },

            requestNewsletterFormTracking(
                url,
                referrer,
                siteHandle,
                languageCode,
            ) {
                const country = (siteHandle = 'default'
                    ? 'mgMotorsEurope'
                    : siteHandle);

                if (checkCCM19Consent() || checkOneTrustConsent()) {
                    // check if you are on production-enviroment
                    // Daisycon pixel
                    if (!window.dataLayer) {
                        window.dataLayer = window.dataLayer || [];
                    }

                    window.dataLayer.push({
                        'campaign-id': '16022',
                        'transaction-id': '19705',
                        'product-name': 'MG',
                        amount: '0',
                        revenue: '0',
                        'compensation-code': 'Newsletter',
                        e1: `${referrer}`,
                    });
                }
            },

            insertTrackingImageLinkedin(source) {
                var img = document.createElement('img');
                img.height = '1';
                img.width = '1';
                img.style = 'display:none;';
                img.src = source;
                var src = document.getElementById('app');
                src.appendChild(img);
            },

            insertTrackingImageAzerion(source) {
                var img = document.createElement('img');
                img.height = '1';
                img.width = '1';
                img.setAttribute(
                    'referrerpolicy',
                    'no-referrer-when-downgrade',
                );
                img.style = 'display:none;';
                img.src = source;
                var src = document.getElementById('app');
                src.appendChild(img);
            },

            insertTrackingImageShowHeroes(source) {
                var img = document.createElement('img');
                img.height = '1';
                img.width = '1';
                img.style = 'display:none;';
                img.src = source;
                var src = document.getElementById('app');
                src.appendChild(img);
            },

            personHasInteracted() {
                if (this.hasInteracted) {
                    window.removeEventListener(
                        'click',
                        this.personHasInteracted,
                    );
                    window.removeEventListener(
                        'scroll',
                        this.personHasInteracted,
                    );
                    return;
                }

                this.hasInteracted = true;
            },

            // This function will replace the input type file chosen file to display to the user.
            replaceInputValue(e) {
                const selectedFile = e.target.value.replace(
                    `C:\\fakepath\\`,
                    '',
                );
                const textValueEl =
                    e.target.parentNode.querySelector('input[type="text"]');
                textValueEl.value = selectedFile;
            },

            initRevealTriggers() {
                const elements = [
                    ...document.querySelectorAll('.a-fade-up'),
                    ...document.querySelectorAll('.a-fade-in'),
                    ...document.querySelectorAll('[data-reveal]'),
                    ...document.querySelectorAll('.opacity-blur'),
                    ...document.querySelectorAll('[data-reveal-staggered]'),
                    ...document.querySelectorAll('.zoomed-image'),
                ];

                elements.forEach((element, index) => {
                    const {
                        revealDelay,
                        revealManually,
                        revealPosition,
                        revealNumberCount,
                        revealStaggered,
                    } = element.dataset;

                    if (revealManually === '') {
                        return;
                    }

                    let startPercentage = revealPosition || '88%';

                    if (revealNumberCount) {
                        startPercentage = '75%';
                    }

                    gsap.timeline({
                        scrollTrigger: {
                            trigger: element,
                            start: `top ${startPercentage}`,
                            onEnter: () => {
                                if (revealNumberCount !== null) {
                                    const numberCounts =
                                        element.querySelectorAll(
                                            '.number-count',
                                        );

                                    if (numberCounts) {
                                        for (
                                            let i = 0;
                                            i < numberCounts.length;
                                            i++
                                        ) {
                                            let zero = { val: 0 };
                                            let num, split, decimals;
                                            const initWidth =
                                                numberCounts[i].offsetWidth;

                                            numberCounts[i].setAttribute(
                                                'data-number',
                                                numberCounts[i].innerText,
                                            );

                                            const makeNum = () =>
                                                /^[0-9,.]*$/.test(
                                                    numberCounts[i].dataset
                                                        .number,
                                                )
                                                    ? parseFloat(
                                                          numberCounts[
                                                              i
                                                          ].dataset.number.replace(
                                                              ',',
                                                              '.',
                                                          ),
                                                      )
                                                    : parseInt(
                                                          numberCounts[i]
                                                              .dataset.number,
                                                      );

                                            num = makeNum();

                                            // const spanNumberWidth = num.toString().replace(/[^0-9]/g, '') ? (window.innerWidth < 1440 ? 12 : 16) * num.toString().replace(/[^0-9]/g, '').length : null;
                                            // const spanPeriodWidth = num.toString().match(/\./g) ? (window.innerWidth < 1440 ? 5 : 7) * num.toString().match(/\./g).length : null;

                                            gsap.set(numberCounts[i], {
                                                width: initWidth,
                                                // width: spanPeriodWidth ? spanNumberWidth + spanPeriodWidth : spanNumberWidth,
                                            });

                                            if (
                                                numberCounts[
                                                    i
                                                ].innerText.includes('.')
                                            ) {
                                                split = (
                                                    numberCounts[i].innerText +
                                                    ''
                                                ).split('.');
                                            } else {
                                                split = (
                                                    numberCounts[i].innerText +
                                                    ''
                                                ).split(',');
                                            }

                                            decimals =
                                                split.length > 1
                                                    ? split[1].length
                                                    : 0;

                                            gsap.to(zero, {
                                                val: num,
                                                duration: 2,
                                                ease: 'power2.out',
                                                onUpdate: function () {
                                                    if (zero) {
                                                        numberCounts[
                                                            i
                                                        ].innerHTML =
                                                            zero.val.toFixed(
                                                                decimals,
                                                            );
                                                    }
                                                },
                                            });
                                        }
                                    }
                                }

                                if (revealStaggered === '') {
                                    let parent = gsap.utils.selector(element);

                                    gsap.to(parent('.staggered-zoomed-image'), {
                                        scale: 1,
                                        duration: 0.3,
                                    });

                                    gsap.to(parent('.dual-block__content'), {
                                        x: 0,
                                        y: 0,
                                        duration: 0.3,
                                        delay: -0.6,
                                    });

                                    gsap.to(parent('.staggered-fade'), {
                                        duration: 1.2,
                                        autoAlpha: 1,
                                        y: 0,
                                        stagger: 0.1,
                                        ease: 'power3.out',
                                    });

                                    return;
                                }

                                if (!revealDelay) {
                                    element.classList.add('--reveal');
                                    return;
                                }

                                gsap.delayedCall(revealDelay, () => {
                                    element.classList.add('--reveal');
                                });
                            },
                        },
                    });
                });

                gsap.delayedCall(3, () => ScrollTrigger.refresh());
            },

            /**
             * Raised when the client window is resizing. Checks if the
             * application has switched to a mobile breakpoint and
             * act accordingly when a switch has indeed occurred.
             *
             * @return {Void}
             */
            onWindowResize: debounce(function () {
                this.isMobile = window.innerWidth < 768;
                EventBus.$emit('window-resize');
                ScrollTrigger.refresh();
            }, 150),
        },

        beforeDestroy() {
            window.addEventListener('resize', this.onWindowResize);
        },
    });
}

initVueJS();
